import { ReactNode, useEffect, useReducer } from 'react';
import { isEmpty } from 'lodash-es';
import { getConversations, getFrontendSettings, getGroupConversations, getNotifications, getPinnedConversations, getUsage, getUserPreferences, getUsersAgents, getUsersCustomGPTs, USAGE_REFRESH_EVENT } from '@/api/api';
import { dedupeConversationsById } from '@/utils/conversations';
import { Agent, CustomGPT, DEFAULT_PERSONALIZATION, Notification } from '@/api/models';
import { appStateReducer } from './AppReducer';
import { AppState, AppStateContext } from './AppStateContext';
import { chatModels } from '@/constants/models';
import { applyPersistedModelSelection } from '@/utils/modelSelection';

// Initially set user's theme - set to light mode if they havent toggled to dark mode and their system preference is light
const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
if (localStorage.getItem('sms-gpt_theme') === 'light' || (isEmpty(localStorage.getItem('sms-gpt_theme')) && !isDarkMode)) {
  document.documentElement.setAttribute('data-theme', 'light');
  document.documentElement.classList.remove('dark');
}

// Automatically close the side panel on mobile on page load
if (screen.width <= 700) localStorage.setItem('sms-gpt_sidepanelOpen', '0');

const initialState: AppState = {
  theme: localStorage.getItem('sms-gpt_theme') || (isDarkMode ? 'dark' : 'light') || 'dark',
  currentUser: null,
  userGroups: [],
  sidepanelOpen: (localStorage.getItem('sms-gpt_sidepanelOpen') === '0' ? false : true),
  sidepanelTransitioning: false,
  frontendSettings: null,
  notifications: null,
  weeklyUsage: null,
  searchPopupOpen: false,
  chatHistory: null,
  groupChats: null,
  customGPTs: null,
  customGPTsLoaded: false,
  currentModel: applyPersistedModelSelection((!isEmpty(localStorage.getItem('sms-gpt_model')) ? chatModels.find(model => model.id === localStorage.getItem('sms-gpt_model')) : (chatModels.find(m => m.default) || chatModels[0])) || (chatModels.find(m => m.default) || chatModels[0])),
  currentCustomGPT: null,
  agents: null,
  currentAgent: null,
  chatPanelTransitioned: false,
  currentChat: null,
  convFilesOpen: true,
  fileUpload: null,
  imageUpload: null,
  individualFileChat: null,
  feedbackState: {},
  selectedCitation: null,
  selectedCitations: null,
  selectedConvStarter: null,
  selectedTool: null,
  personalization: DEFAULT_PERSONALIZATION,
  personalizationLoading: true,
  personalizationError: null,
};

type AppStateProviderProps = {
  children: ReactNode;
  isAuthenticated: boolean;
};
  
export const AppStateProvider: React.FC<AppStateProviderProps> = ({ children, isAuthenticated }) => {
  const [state, dispatch] = useReducer(appStateReducer, initialState);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchInitialData = async () => {
      try {
        let personalizationLoadError: Error | undefined;
        // Parallel data fetching
        const [frontendSettings, notifications, usage, customGPTs, agents, chatHistory, pinnedChats, groupChats, personalization] = await Promise.all([
          getFrontendSettings(),
          getNotifications(),
          getUsage(),
          getUsersCustomGPTs(),
          getUsersAgents(),
          getConversations(),
          getPinnedConversations(),
          getGroupConversations(),
          getUserPreferences().catch((error: unknown) => {
            personalizationLoadError = error instanceof Error ? error : new Error('Unable to load personalization settings.');
            return null;
          }),
        ]);

        // Pinned chats are loaded in full (not paged) so they are always visible;
        // merge them with the first page, de-duping the recent pins present in both.
        const mergedChatHistory = (chatHistory === null && pinnedChats === null)
          ? null
          : dedupeConversationsById([...(pinnedChats || []), ...(chatHistory || [])]);

        // Dispatch actions for each successful fetch
        dispatch({type: 'LOAD_FRONTEND_SETTINGS', payload: frontendSettings as any });
        dispatch({ type: 'LOAD_NOTIFICATIONS', payload: notifications as unknown as Notification[] });
        dispatch({ type: 'LOAD_USAGE', payload: usage });
        dispatch({ type: 'LOAD_CUSTOM_GPTS', payload: customGPTs as unknown as CustomGPT[] });
        dispatch({ type: 'SET_CUSTOM_GPTS_LOADED', payload: true });
        dispatch({ type: 'LOAD_AGENTS', payload: agents as unknown as Agent[] });
        dispatch({ type: 'LOAD_CHAT_HISTORY', payload: mergedChatHistory });
        dispatch({ type: 'LOAD_GROUP_CHATS', payload: groupChats || null });
        dispatch({ type: 'LOAD_PERSONALIZATION', payload: personalization || DEFAULT_PERSONALIZATION });
        dispatch({ type: 'SET_PERSONALIZATION_LOADING', payload: false });
        if (!personalization) dispatch({
          type: 'SET_PERSONALIZATION_ERROR',
          payload: personalizationLoadError || 'Unable to load personalization settings.',
        });

        // Set current custom GPT if exists in local storage (only if the user actually has it in their list)
        if (!isEmpty(localStorage.getItem('sms-gpt_cgpt')) && customGPTs && customGPTs.length > 0) {
          const savedCGPT = customGPTs.find(cgpt => cgpt.id === localStorage.getItem('sms-gpt_cgpt'));
          if (savedCGPT) dispatch({ type: 'SET_CURRENT_CUSTOM_GPT', payload: savedCGPT });
        }
      } catch (error) {
        console.error("There was an issue fetching initial data:", error);
        dispatch({ type: 'LOAD_FRONTEND_SETTINGS', payload: null });
        dispatch({ type: 'LOAD_NOTIFICATIONS', payload: [] });
        dispatch({ type: 'LOAD_USAGE', payload: null });
        dispatch({ type: 'LOAD_CUSTOM_GPTS', payload: [] });
        dispatch({ type: 'LOAD_AGENTS', payload: [] });
        dispatch({ type: 'LOAD_CHAT_HISTORY', payload: null });
        dispatch({ type: 'LOAD_GROUP_CHATS', payload: [] });
        dispatch({ type: 'LOAD_PERSONALIZATION', payload: DEFAULT_PERSONALIZATION });
        dispatch({ type: 'SET_PERSONALIZATION_LOADING', payload: false });
        dispatch({ type: 'SET_PERSONALIZATION_ERROR', payload: 'Unable to load personalization settings.' });
      }
    }

    fetchInitialData();
  }, [isAuthenticated]);

  // Keep usage current after each chat completes. The event is fired by
  // notifyUsageChanged(), so the listener has to live above the UI that
  // displays usage (which is only mounted while the settings dialog is open).
  useEffect(() => {
    if (!isAuthenticated) return;

    const refreshUsage = async () => {
      const usage = await getUsage();
      dispatch({ type: 'LOAD_USAGE', payload: usage });
    };

    window.addEventListener(USAGE_REFRESH_EVENT, refreshUsage);
    return () => window.removeEventListener(USAGE_REFRESH_EVENT, refreshUsage);
  }, [isAuthenticated]);

  return (
    <AppStateContext.Provider value={{ state, dispatch }}>
      {children}
    </AppStateContext.Provider>
  );
};
